import React, {useMemo} from 'react';
import {
    BrowseEventTrackingProvider,
    TrackableDiscoveryItem,
    DiscoveryItemTypeOptions,
} from '@udemy-v2/react-browse-event-tracking';
import {
    LoadingStatus,
    RecommendationItem,
    RecommendationSourceObjectType,
    RecommendationsProvider,
    useRecommendationsSections,
} from '@udemy-v2/react-recommendations';
import {StudentsAlsoBought} from './students-also-bought.react-component';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {BrowseCourse} from '@udemy-v2/browse-course';
import {useUserHasOrganization} from '@/lib/hooks/use-user-has-organization';
import {useI18n} from '@udemy-v2/i18n';

// This is the maximum number of courses that will be shown in the Students Also Bought section.
const RECOMMENDATIONS_ITEM_COUNT_PER_UNIT = 18;

/**
 * This wrapper is used to wrap the StudentsAlsoBought component with the RecommendationsProvider by passing the correct course.id value and contextId.
 * For UB CLPs, we use the 'org-landing-page' context to ensure that we load "Students also took"-style recommendations for organization users
 */
export const StudentsAlsoBoughtRecommendationsWrapper = () => {
    const hasOrganization = useUserHasOrganization();
    const course = useClpCourse();

    const contextId = hasOrganization ? 'org-landing-page' : 'landing-page';
    const contextInput = useMemo(
        () => ({
            contextId,
            sourceObjects: [{id: course.id, type: RecommendationSourceObjectType.Course}],
            requestedItemCountPerUnit: RECOMMENDATIONS_ITEM_COUNT_PER_UNIT,
        }),
        [contextId, course.id],
    );

    return (
        <RecommendationsProvider
            key={contextId}
            contextInput={contextInput}
            includeAvailableSubscriptionInfo={true}
        >
            <BrowseEventTrackingProvider
                browseEventTrackingContext={{
                    relatedSourceId: course.id,
                    relatedSourceType: DiscoveryItemTypeOptions.COURSE,
                }}
            >
                <StudentsAlsoBoughtWrapper />
            </BrowseEventTrackingProvider>
        </RecommendationsProvider>
    );
};

/**
 * This wrapper is used to wrap the StudentsAlsoBought component with the logic needed to consume recos data from the RecommendationsProvider.
 */
export const StudentsAlsoBoughtWrapper = () => {
    const {gettext} = useI18n();
    const {status, data: recommendationsSections} = useRecommendationsSections();
    const recommendations =
        status === LoadingStatus.LOADED ? recommendationsSections[0] : undefined;

    const title = recommendations?.title ?? gettext('Students also bought');

    const courseRecommendations = useMemo(() => {
        if (!recommendations) return [];

        const courses: TrackableDiscoveryItem<BrowseCourse>[] = [];
        if ('items' in recommendations) {
            recommendations.items.forEach((item: RecommendationItem) => {
                if (item && typeof item === 'object' && 'course' in item && item.course) {
                    courses.push({
                        ...(item.course as BrowseCourse),
                        trackingId: item.trackingId,
                        frontendTrackingId: item.frontendTrackingId,
                    });
                }
            });
        }
        return courses;
    }, [recommendations]);

    if (status !== LoadingStatus.LOADED) {
        return null;
    }

    return <StudentsAlsoBought courses={courseRecommendations} title={title} />;
};
